import { Injectable } from '@angular/core';
import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse
} from '@angular/common/http';
import { Storage } from '@ionic/storage-angular';
import { Observable, from, of, throwError, TimeoutError } from 'rxjs';
import { catchError, switchMap, tap, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const PREFIJO = 'http_cache';
// Con señal débil la petición puede quedarse colgada; pasado este tiempo usamos la copia local
const TIMEOUT_MS = 15_000;
// Colecciones cuyo listado (populate=*) trae el mismo contenido que el detalle de cada elemento
const COLECCIONES_CON_DETALLE = ['orden-operativas', 'visita-cotizacions'];

/**
 * Guarda en IndexedDB cada respuesta GET de la API y la devuelve cuando no hay conexión.
 * Funciona en la APK (Capacitor), donde el service worker de Angular no interviene.
 */
@Injectable()
export class OfflineCacheInterceptor implements HttpInterceptor {

  private db?: Promise<Storage>;

  constructor(private storage: Storage) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.method !== 'GET' || !req.url.startsWith(environment.apiUrl)) {
      return next.handle(req);
    }

    const url = req.urlWithParams;

    return next.handle(req).pipe(
      timeout({ each: TIMEOUT_MS }),
      tap(event => {
        if (event instanceof HttpResponse && event.ok) this.guardar(url, event.body);
      }),
      catchError(error => {
        if (!this.esFallaDeRed(error)) return throwError(() => error);

        return from(this.leer(url)).pipe(
          switchMap(body => {
            if (body === undefined) {
              console.warn(`📡 Offline: no hay copia local para ${url}`);
              return throwError(() => error);
            }
            console.log(`📡 Offline: respuesta local para ${url}`);
            return of(new HttpResponse({ status: 200, body, url }));
          })
        );
      })
    );
  }

  private esFallaDeRed(error: unknown): boolean {
    if (error instanceof TimeoutError) return true;
    // 0 = sin red; 504 = el service worker no tenía copia; 5xx = servidor caído
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
  }

  private async guardar(url: string, body: any) {
    try {
      const db = await this.getDb();
      await db.set(this.clave(url), body);

      // Un listado con populate=* también sirve como detalle de cada elemento,
      // así se puede abrir offline una orden/visita aunque nunca se haya abierto con internet
      const coleccion = COLECCIONES_CON_DETALLE.find(c => url.startsWith(`${environment.apiUrl}/${c}?`));
      if (coleccion && url.includes('populate=*') && Array.isArray(body?.data)) {
        for (const item of body.data) {
          if (!item?.documentId) continue;
          const urlDetalle = `${environment.apiUrl}/${coleccion}/${item.documentId}?populate=*`;
          await db.set(this.clave(urlDetalle), { data: item, meta: {} });
        }
      }
    } catch (error) {
      console.warn('No se pudo guardar la respuesta en caché local', error);
    }
  }

  private async leer(url: string): Promise<any> {
    try {
      const body = await (await this.getDb()).get(this.clave(url));
      return body ?? undefined;
    } catch {
      return undefined;
    }
  }

  // La clave incluye el usuario para que en un dispositivo compartido nadie vea datos de otro
  private clave(url: string): string {
    let userId = 'anon';
    try {
      userId = JSON.parse(localStorage.getItem('user') || '{}').id ?? 'anon';
    } catch { }
    return `${PREFIJO}:${userId}:${url}`;
  }

  private getDb(): Promise<Storage> {
    return this.db ??= this.storage.create();
  }
}
